/**
 * ShopRedirect.jsx
 *
 * Tier 1 Shopify integration — clean external redirect.
 * Visitors hitting getturfpulse.com/shop are forwarded to the Shopify
 * storefront. Destination URL is configurable via REACT_APP_SHOP_URL
 * (falls back to the canonical turfpulse-merch.myshopify.com).
 *
 * UX notes:
 *  - Renders a brief "Taking you to the shop…" message in case the
 *    redirect is delayed (low-bandwidth, slow phones).
 *  - Uses window.location.replace so the redirect is not added to the
 *    browser history (back button returns to the previous TurfPulse
 *    page, not to /shop).
 */
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_SHOP_URL = 'https://turfpulse-merch.myshopify.com';

const ShopRedirect = () => {
  const target = (process.env.REACT_APP_SHOP_URL || DEFAULT_SHOP_URL).trim();

  useEffect(() => {
    // Defer one frame so the page paints before the redirect fires.
    const t = window.setTimeout(() => {
      window.location.replace(target);
    }, 50);
    return () => window.clearTimeout(t);
  }, [target]);

  return (
    <>
      <Helmet>
        <title>TurfPulse Shop — Lawn Nerd Apparel & Founder Gear</title>
        <meta
          name="description"
          content="Founder-built gear for data-driven lawn enthusiasts. Tees, hats, and field-tested merch from the team running soil probes in their own front yard."
        />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={target} />
      </Helmet>
      <main
        className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6"
        data-testid="shop-redirect-page"
      >
        <div className="text-2xl font-semibold mb-2">Taking you to the shop…</div>
        <p className="text-sm text-muted-foreground mb-6">
          Hold tight — redirecting to <span className="font-mono">{target.replace(/^https?:\/\//, '')}</span>
        </p>
        <a
          href={target}
          className="text-sm underline hover:text-foreground"
          data-testid="shop-redirect-manual-link"
        >
          Click here if it doesn't redirect automatically
        </a>
      </main>
    </>
  );
};

export default ShopRedirect;
